import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Globe, RefreshCw, Settings, ChevronDown, ChevronUp,
  Play, CheckCircle, AlertTriangle, XCircle, Clock,
  Plus, Trash2, Save, ExternalLink, FileText, Info
} from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../../services/api';

const FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'bi-weekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
];

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function RunStatusBadge({ run }) {
  if (run.status === 'running') {
    return (
      <span data-testid="run-status-running" className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
        <RefreshCw size={10} className="animate-spin" /> Running
      </span>
    );
  }
  if (run.status === 'failed') {
    return (
      <span data-testid="run-status-failed" className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
        <XCircle size={10} /> Failed
      </span>
    );
  }
  if (!run.change_summary?.has_changes) {
    return (
      <span data-testid="run-status-no-changes" className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
        <CheckCircle size={10} /> No changes
      </span>
    );
  }
  if (run.master_brief_updated) {
    return (
      <span data-testid="run-status-applied" className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <CheckCircle size={10} /> Applied
      </span>
    );
  }
  if (run.reviewed) {
    return (
      <span data-testid="run-status-dismissed" className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
        <CheckCircle size={10} /> Dismissed
      </span>
    );
  }
  return (
    <span data-testid="run-status-changes" className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
      <AlertTriangle size={10} /> Changes - review
    </span>
  );
}

// ── Toggle Switch ─────────────────────────────────────────────────────────────

function Toggle({ checked, onChange, disabled, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      data-testid={testId}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${
        checked ? 'bg-blue-600' : 'bg-gray-300'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-4.5' : 'translate-x-0.5'
        }`}
        style={{ transform: checked ? 'translateX(18px)' : 'translateX(2px)' }}
      />
    </button>
  );
}

// ── Run Detail Modal ──────────────────────────────────────────────────────────

function RunDetailModal({ run, onClose, onDismiss, onApply, onConfirmApply, applyJobId, applyJob }) {
  const [showPages, setShowPages] = useState(false);
  const [showApplyConfirm, setShowApplyConfirm] = useState(false);
  const [editedBrief, setEditedBrief] = useState('');

  const cs = run?.change_summary || {};
  const isApplying = applyJob?.status === 'running';
  const applyDone = applyJob?.status === 'complete';
  const applyFailed = applyJob?.status === 'failed';

  useEffect(() => {
    if (applyDone && applyJob?.result?.proposed_brief) {
      setEditedBrief(applyJob.result.proposed_brief);
    }
  }, [applyDone, applyJob]);

  if (!run) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.55)' }}>
      <div
        data-testid="run-detail-modal"
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl shadow-2xl"
        style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4" style={{ background: 'hsl(var(--card))', borderBottom: '1px solid hsl(var(--border))' }}>
          <div>
            <div className="text-sm font-semibold" style={{ color: 'hsl(var(--foreground))' }}>
              Crawl Run - {fmtDate(run.started_at)}
            </div>
            <div className="text-xs mt-0.5" style={{ color: 'hsl(var(--muted-foreground))' }}>
              {run.pages_crawled} pages crawled across {(run.sites || []).join(', ')}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-gray-100 transition-colors">
            <XCircle size={16} style={{ color: 'hsl(var(--muted-foreground))' }} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* AI Change Summary */}
          {cs.has_changes ? (
            <>
              {/* Detect failed AI summary */}
              {cs.ai_narrative?.startsWith('Change summary generation failed') ? (
                <div className="rounded-lg p-4 flex items-start gap-3" style={{ background: '#fef9c3', border: '1px solid #fde68a' }}>
                  <AlertTriangle size={15} className="mt-0.5 flex-shrink-0 text-amber-600" />
                  <div>
                    <div className="text-xs font-semibold text-amber-800 mb-1">AI summary unavailable for this run</div>
                    <div className="text-xs text-amber-700">
                      This was the first successful crawl (baseline), so there were no prior page snapshots to compare against.
                      Run a second crawl and the AI will correctly identify content changes between runs.
                    </div>
                  </div>
                </div>
              ) : (
              <div className="rounded-lg p-4 space-y-3" style={{ background: 'hsl(var(--muted))', border: '1px solid hsl(var(--border))' }}>
                <div className="flex items-start gap-2">
                  {cs.update_recommended ? (
                    <AlertTriangle size={15} className="mt-0.5 flex-shrink-0 text-amber-600" />
                  ) : (
                    <CheckCircle size={15} className="mt-0.5 flex-shrink-0 text-green-600" />
                  )}
                  <div>
                    <div className="text-xs font-semibold mb-1" style={{ color: 'hsl(var(--foreground))' }}>
                      {cs.update_recommended ? 'Recommendation: Update Master Brief' : 'No update needed'}
                    </div>
                    <div className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>{cs.update_reason}</div>
                  </div>
                </div>
              </div>
              )}

              {!cs.ai_narrative?.startsWith('Change summary generation failed') && (
              <div>
                <div className="text-xs font-semibold mb-2" style={{ color: 'hsl(var(--foreground))' }}>AI Change Summary</div>
                <p className="text-xs leading-relaxed" style={{ color: 'hsl(var(--muted-foreground))' }}>{cs.ai_narrative}</p>
              </div>
              )}

              {cs.key_additions?.length > 0 && (
                <div>
                  <div className="text-xs font-semibold mb-1.5 text-green-700">Key Additions</div>
                  <ul className="space-y-1">
                    {cs.key_additions.map((a, i) => (
                      <li key={i} className="text-xs flex gap-2" style={{ color: 'hsl(var(--muted-foreground))' }}>
                        <span className="text-green-600 font-bold flex-shrink-0">+</span>{a}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {cs.key_removals?.length > 0 && (
                <div>
                  <div className="text-xs font-semibold mb-1.5 text-red-600">Key Removals / Changes</div>
                  <ul className="space-y-1">
                    {cs.key_removals.map((r, i) => (
                      <li key={i} className="text-xs flex gap-2" style={{ color: 'hsl(var(--muted-foreground))' }}>
                        <span className="text-red-500 font-bold flex-shrink-0">−</span>{r}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {cs.resource_impacts?.length > 0 && (
                <div>
                  <div className="text-xs font-semibold mb-2" style={{ color: 'hsl(var(--foreground))' }}>Resource Recommendations</div>
                  <div className="space-y-2">
                    {cs.resource_impacts.map((impact, i) => (
                      <div key={i} className="rounded-lg px-3 py-2.5 text-xs" style={{
                        background: impact.action === 'update_existing' ? 'rgba(8,145,178,0.06)' :
                                    impact.action === 'create_new' ? 'rgba(22,163,74,0.06)' : 'hsl(var(--muted))',
                        border: `1px solid ${impact.action === 'update_existing' ? 'rgba(8,145,178,0.2)' :
                                              impact.action === 'create_new' ? 'rgba(22,163,74,0.2)' : 'hsl(var(--border))'}`,
                      }}>
                        <div className="flex items-start gap-2">
                          <span className="font-semibold flex-shrink-0" style={{
                            color: impact.action === 'update_existing' ? '#0891b2' :
                                   impact.action === 'create_new' ? '#16a34a' : '#64748b'
                          }}>
                            {impact.action === 'update_existing' ? 'Update:' :
                             impact.action === 'create_new' ? 'New:' : 'Note:'}
                          </span>
                          <div>
                            <div style={{ color: 'hsl(var(--foreground))' }}>{impact.change}</div>
                            {impact.existing_resource && (
                              <div className="mt-0.5" style={{ color: 'hsl(var(--muted-foreground))' }}>
                                Existing resource: <strong>{impact.existing_resource}</strong>
                              </div>
                            )}
                            <div className="mt-0.5 italic" style={{ color: 'hsl(var(--muted-foreground))' }}>{impact.reason}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="rounded-lg p-4 flex items-center gap-3" style={{ background: 'hsl(var(--muted))' }}>
              <CheckCircle size={16} className="text-green-500" />
              <span className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>No content changes detected since the last crawl.</span>
            </div>
          )}

          {/* Changed pages */}
          {run.changed_pages?.length > 0 && (
            <div>
              <button
                onClick={() => setShowPages(v => !v)}
                className="flex items-center gap-1.5 text-xs font-medium mb-2 hover:underline"
                style={{ color: 'hsl(var(--foreground))' }}
              >
                {showPages ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                Changed pages ({run.changed_pages.length})
              </button>
              {showPages && (
                <div className="space-y-1 max-h-40 overflow-y-auto rounded-lg p-3" style={{ background: 'hsl(var(--muted))', border: '1px solid hsl(var(--border))' }}>
                  {run.changed_pages.map((p, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${
                        p.change_type === 'new' ? 'bg-green-100 text-green-700' :
                        p.change_type === 'removed' ? 'bg-red-100 text-red-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>{p.change_type}</span>
                      <span className="text-xs truncate flex-1" style={{ color: 'hsl(var(--muted-foreground))' }}>{p.title || p.url}</span>
                      <a href={p.url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                        <ExternalLink size={10} style={{ color: 'hsl(var(--muted-foreground))' }} />
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Apply flow */}
          {cs.has_changes && !run.master_brief_updated && !run.reviewed && !cs.ai_narrative?.startsWith('Change summary generation failed') && (
            <>
              {!showApplyConfirm && (
                <div className="flex gap-2 pt-2" style={{ borderTop: '1px solid hsl(var(--border))' }}>
                  <button
                    data-testid="btn-dismiss-run"
                    onClick={() => { onDismiss(run.id); onClose(); }}
                    className="flex-1 py-2 rounded-lg text-xs font-medium transition-colors"
                    style={{ background: 'hsl(var(--muted))', color: 'hsl(var(--muted-foreground))' }}
                  >
                    Dismiss
                  </button>
                  {cs.update_recommended && (
                    <button
                      data-testid="btn-apply-to-brief"
                      onClick={() => setShowApplyConfirm(true)}
                      className="flex-1 py-2 rounded-lg text-xs font-semibold text-white transition-colors"
                      style={{ background: '#1e3a5f' }}
                    >
                      Apply to Master Brief
                    </button>
                  )}
                </div>
              )}

              {showApplyConfirm && !isApplying && !applyDone && (
                <div className="rounded-lg p-4 space-y-3" style={{ background: 'hsl(var(--muted))', border: '1px solid hsl(var(--border))' }}>
                  <div className="flex items-start gap-2">
                    <Info size={14} className="mt-0.5 flex-shrink-0 text-blue-600" />
                    <p className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>
                      Claude will update the Master Brief based on these changes.
                      All 26 prompt cards will show <strong>Update available</strong> after this.
                      You can review the new text before it is saved.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setShowApplyConfirm(false)} className="flex-1 py-1.5 rounded-lg text-xs" style={{ background: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }}>Cancel</button>
                    <button
                      data-testid="btn-confirm-apply"
                      onClick={() => onApply(run.id)}
                      className="flex-1 py-1.5 rounded-lg text-xs font-semibold text-white"
                      style={{ background: '#0891b2' }}
                    >
                      Continue
                    </button>
                  </div>
                </div>
              )}

              {isApplying && (
                <div className="flex items-center gap-3 py-3" style={{ color: 'hsl(var(--muted-foreground))' }}>
                  <RefreshCw size={14} className="animate-spin" />
                  <span className="text-xs">{applyJob?.progress || 'Claude is working...'}</span>
                </div>
              )}

              {applyFailed && (
                <div className="rounded-lg p-3 bg-red-50 border border-red-200">
                  <p className="text-xs text-red-700">Apply failed: {applyJob?.error}</p>
                </div>
              )}

              {applyDone && (
                <div className="space-y-3">
                  <div className="text-xs font-semibold" style={{ color: 'hsl(var(--foreground))' }}>Review proposed Master Brief</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs font-medium mb-1.5 text-gray-500">Current</div>
                      <textarea
                        readOnly
                        value={applyJob.result?.current_brief || ''}
                        className="w-full h-52 text-xs p-2 rounded-lg resize-none font-mono"
                        style={{ background: 'hsl(var(--muted))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--muted-foreground))' }}
                      />
                    </div>
                    <div>
                      <div className="text-xs font-medium mb-1.5 text-green-600">Proposed (editable)</div>
                      <textarea
                        value={editedBrief}
                        onChange={e => setEditedBrief(e.target.value)}
                        data-testid="proposed-brief-editor"
                        className="w-full h-52 text-xs p-2 rounded-lg resize-none font-mono"
                        style={{ background: 'hsl(var(--background))', border: '1px solid #0891b2', color: 'hsl(var(--foreground))' }}
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={onClose} className="flex-1 py-2 rounded-lg text-xs" style={{ background: 'hsl(var(--muted))' }}>Cancel</button>
                    <button
                      data-testid="btn-save-master-brief"
                      onClick={() => onConfirmApply(run.id, editedBrief)}
                      className="flex-1 py-2 rounded-lg text-xs font-semibold text-white"
                      style={{ background: '#1e3a5f' }}
                    >
                      Save Master Brief
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* For baseline/failed-summary runs: just show a dismiss button */}
          {cs.has_changes && !run.reviewed && cs.ai_narrative?.startsWith('Change summary generation failed') && (
            <div className="flex justify-end pt-2" style={{ borderTop: '1px solid hsl(var(--border))' }}>
              <button
                data-testid="btn-dismiss-baseline"
                onClick={() => { onDismiss(run.id); onClose(); }}
                className="px-4 py-2 rounded-lg text-xs font-medium transition-colors"
                style={{ background: 'hsl(var(--muted))', color: 'hsl(var(--muted-foreground))' }}
              >
                Mark as reviewed
              </button>
            </div>
          )}

          {run.master_brief_updated && (
            <div className="rounded-lg p-3 flex items-center gap-2 bg-green-50 border border-green-200">
              <CheckCircle size={14} className="text-green-600" />
              <span className="text-xs text-green-700">Master Brief was updated from this run.</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Domain Settings Card ──────────────────────────────────────────────────────

function DomainCard({ site, idx, onChange, onRemove, isDefault }) {
  const [showExcludes, setShowExcludes] = useState(false);

  return (
    <div
      data-testid={`domain-card-${site.domain}`}
      className="rounded-xl p-5 space-y-4"
      style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Globe size={14} style={{ color: '#0891b2' }} />
          <span className="text-sm font-semibold" style={{ color: 'hsl(var(--foreground))' }}>
            {site.domain || 'New Domain'}
          </span>
          {isDefault && (
            <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'hsl(var(--muted))', color: 'hsl(var(--muted-foreground))' }}>default</span>
          )}
        </div>
        <button
          data-testid={`remove-domain-${idx}`}
          onClick={() => onRemove(idx)}
          className="p-1.5 rounded-md text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
          title="Remove domain"
        >
          <Trash2 size={13} />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium block mb-1" style={{ color: 'hsl(var(--muted-foreground))' }}>Domain</label>
          <input
            data-testid={`input-domain-${idx}`}
            type="text"
            value={site.domain || ''}
            onChange={e => onChange(idx, 'domain', e.target.value)}
            placeholder="example.com"
            className="w-full px-2.5 py-1.5 rounded-lg text-xs"
            style={{ background: 'hsl(var(--background))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--foreground))' }}
          />
        </div>
        <div>
          <label className="text-xs font-medium block mb-1" style={{ color: 'hsl(var(--muted-foreground))' }}>Start URL</label>
          <input
            data-testid={`input-start-url-${idx}`}
            type="url"
            value={site.start_url || ''}
            onChange={e => onChange(idx, 'start_url', e.target.value)}
            placeholder="https://www.example.com"
            className="w-full px-2.5 py-1.5 rounded-lg text-xs"
            style={{ background: 'hsl(var(--background))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--foreground))' }}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium" style={{ color: 'hsl(var(--muted-foreground))' }}>Enabled</span>
          <Toggle
            checked={site.enabled !== false}
            onChange={v => onChange(idx, 'enabled', v)}
            testId={`toggle-enabled-${idx}`}
          />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium" style={{ color: 'hsl(var(--muted-foreground))' }}>Auto crawl</span>
          <Toggle
            checked={!!site.automated}
            onChange={v => onChange(idx, 'automated', v)}
            testId={`toggle-automated-${idx}`}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium block mb-1" style={{ color: 'hsl(var(--muted-foreground))' }}>Frequency</label>
          <select
            data-testid={`select-frequency-${idx}`}
            value={site.frequency || 'weekly'}
            onChange={e => onChange(idx, 'frequency', e.target.value)}
            disabled={!site.automated}
            className="w-full px-2.5 py-1.5 rounded-lg text-xs disabled:opacity-50"
            style={{ background: 'hsl(var(--background))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--foreground))' }}
          >
            {FREQUENCIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium block mb-1" style={{ color: 'hsl(var(--muted-foreground))' }}>Max pages</label>
          <input
            data-testid={`input-max-pages-${idx}`}
            type="number"
            min="10"
            max="500"
            value={site.max_pages || 500}
            onChange={e => onChange(idx, 'max_pages', parseInt(e.target.value) || 500)}
            className="w-full px-2.5 py-1.5 rounded-lg text-xs"
            style={{ background: 'hsl(var(--background))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--foreground))' }}
          />
        </div>
      </div>

      {site.automated && site.next_crawl_at && (
        <div className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>
          <Clock size={10} className="inline mr-1" />
          Next scheduled: <strong>{fmtDate(site.next_crawl_at)}</strong>
        </div>
      )}

      {/* Exclude patterns */}
      <div>
        <button
          onClick={() => setShowExcludes(v => !v)}
          className="flex items-center gap-1 text-xs font-medium mb-1"
          style={{ color: 'hsl(var(--muted-foreground))' }}
        >
          {showExcludes ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
          Exclude patterns ({(site.exclude_patterns || []).length})
        </button>
        {showExcludes && (
          <div>
            <textarea
              data-testid={`input-excludes-${idx}`}
              value={(site.exclude_patterns || []).join('\n')}
              onChange={e => onChange(idx, 'exclude_patterns', e.target.value.split('\n').map(s => s.trim()).filter(Boolean))}
              rows={4}
              placeholder="/pricing&#10;/login&#10;/admin"
              className="w-full px-2.5 py-1.5 rounded-lg text-xs font-mono resize-none"
              style={{ background: 'hsl(var(--background))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--foreground))' }}
            />
            <p className="text-xs mt-1" style={{ color: 'hsl(var(--muted-foreground))' }}>One URL path fragment per line. Any page whose path contains this string is skipped.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function CrawlManagerPage() {
  const [activeTab, setActiveTab] = useState('overview');
  const [config, setConfig] = useState(null);
  const [settingsForm, setSettingsForm] = useState(null);
  const [settingsDirty, setSettingsDirty] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(true);

  const [crawlJobId, setCrawlJobId] = useState(null);
  const [crawlJob, setCrawlJob] = useState(null);

  const [selectedRunId, setSelectedRunId] = useState(null);
  const [selectedRun, setSelectedRun] = useState(null);
  const [runDetailLoading, setRunDetailLoading] = useState(false);

  const [applyJobId, setApplyJobId] = useState(null);
  const [applyJob, setApplyJob] = useState(null);

  const pollRef = useRef(null);
  const applyPollRef = useRef(null);

  // ── Data loading ─────────────────────────────────────────────────────────────

  const loadConfig = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/admin/crawl/config');
      setConfig(data);
      setSettingsForm(JSON.parse(JSON.stringify(data)));
    } catch (e) {
      toast.error('Failed to load crawl configuration');
    }
  }, []);

  const loadRuns = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/admin/crawl/runs');
      setRuns(data.runs || []);
    } catch (e) {
      // silently fail on polling
    } finally {
      setRunsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
    loadRuns();
  }, [loadConfig, loadRuns]);

  // ── Crawl job polling ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!crawlJobId) return;

    const poll = async () => {
      try {
        const { data } = await apiClient.get(`/admin/crawl/jobs/${crawlJobId}`);
        setCrawlJob(data);
        if (data.status === 'complete' || data.status === 'failed') {
          clearInterval(pollRef.current);
          setCrawlJobId(null);
          loadRuns();
          loadConfig();
          if (data.status === 'complete') {
            const result = data.result || {};
            if (result.has_changes) {
              toast.success(`Crawl complete - ${result.pages_changed + result.pages_new + result.pages_removed} changes found`);
            } else {
              toast.success('Crawl complete - no content changes detected');
            }
          } else {
            toast.error(`Crawl failed: ${data.error}`);
          }
        }
      } catch (e) {
        clearInterval(pollRef.current);
        setCrawlJobId(null);
      }
    };

    poll();
    pollRef.current = setInterval(poll, 3000);
    return () => clearInterval(pollRef.current);
  }, [crawlJobId, loadRuns, loadConfig]);

  // ── Apply job polling ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!applyJobId) return;

    const poll = async () => {
      try {
        const { data } = await apiClient.get(`/admin/crawl/jobs/${applyJobId}`);
        setApplyJob(data);
        if (data.status === 'complete' || data.status === 'failed') {
          clearInterval(applyPollRef.current);
          if (data.status === 'failed') {
            toast.error(`Apply failed: ${data.error}`);
          }
        }
      } catch (e) {
        clearInterval(applyPollRef.current);
      }
    };

    poll();
    applyPollRef.current = setInterval(poll, 3000);
    return () => clearInterval(applyPollRef.current);
  }, [applyJobId]);

  // ── Handlers ──────────────────────────────────────────────────────────────────

  const handleCrawlNow = async () => {
    try {
      const { data } = await apiClient.post('/admin/crawl/trigger', {});
      setCrawlJobId(data.job_id);
      setCrawlJob({ status: 'running', progress: 'Starting crawl...' });
      toast.info('Crawl started');
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to start crawl';
      toast.error(msg);
    }
  };

  const handleOpenRun = async (runId) => {
    setSelectedRunId(runId);
    setRunDetailLoading(true);
    setApplyJobId(null);
    setApplyJob(null);
    try {
      const { data } = await apiClient.get(`/admin/crawl/runs/${runId}`);
      setSelectedRun(data);
    } catch (e) {
      toast.error('Failed to load run details');
    } finally {
      setRunDetailLoading(false);
    }
  };

  const handleDismiss = async (runId) => {
    try {
      await apiClient.post(`/admin/crawl/runs/${runId}/dismiss`);
      toast.success('Run dismissed');
      loadRuns();
    } catch (e) {
      toast.error('Failed to dismiss run');
    }
  };

  const handleApply = async (runId) => {
    try {
      const { data } = await apiClient.post(`/admin/crawl/runs/${runId}/apply`);
      setApplyJobId(data.apply_job_id);
      setApplyJob({ status: 'running', progress: 'Starting Master Brief update...' });
    } catch (e) {
      toast.error('Failed to start apply job');
    }
  };

  const handleConfirmApply = async (runId, masterBrief) => {
    try {
      await apiClient.post(`/admin/crawl/runs/${runId}/confirm-apply`, { master_brief: masterBrief });
      toast.success('Master Brief updated. All prompt cards now show Update available.');
      setSelectedRun(null);
      setSelectedRunId(null);
      loadRuns();
    } catch (e) {
      toast.error('Failed to save Master Brief');
    }
  };

  // ── Settings handlers ─────────────────────────────────────────────────────────

  const handleSiteChange = (idx, field, value) => {
    setSettingsForm(prev => {
      const sites = [...prev.sites];
      sites[idx] = { ...sites[idx], [field]: value };
      return { ...prev, sites };
    });
    setSettingsDirty(true);
  };

  const handleAddDomain = () => {
    setSettingsForm(prev => ({
      ...prev,
      sites: [...prev.sites, {
        domain: '',
        start_url: 'https://',
        exclude_patterns: ['/pricing', '/login', '/admin'],
        enabled: true,
        automated: false,
        frequency: 'weekly',
        max_pages: 500,
        request_delay_seconds: 1.5,
        last_crawl_at: null,
        next_crawl_at: null,
      }],
    }));
    setSettingsDirty(true);
  };

  const handleRemoveDomain = (idx) => {
    setSettingsForm(prev => {
      const sites = prev.sites.filter((_, i) => i !== idx);
      return { ...prev, sites };
    });
    setSettingsDirty(true);
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const { data } = await apiClient.put('/admin/crawl/config', settingsForm);
      setConfig(data);
      setSettingsForm(JSON.parse(JSON.stringify(data)));
      setSettingsDirty(false);
      toast.success('Settings saved');
    } catch (e) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  // ── Domain status derived from config ─────────────────────────────────────────

  const domainStatuses = (config?.sites || []).filter(s => s.enabled !== false).map(site => ({
    domain: site.domain,
    lastCrawled: site.last_crawl_at,
    nextScheduled: site.next_crawl_at,
    automated: site.automated,
    frequency: site.frequency,
  }));

  const isRunning = crawlJob?.status === 'running';

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'hsl(var(--foreground))' }}>Website Sync</h1>
          <p className="text-sm mt-0.5" style={{ color: 'hsl(var(--muted-foreground))' }}>
            Crawl public websites, detect changes, and keep the Master Brief up to date.
          </p>
        </div>
        <button
          data-testid="btn-crawl-now"
          onClick={handleCrawlNow}
          disabled={isRunning}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60 transition-opacity"
          style={{ background: '#1e3a5f' }}
        >
          {isRunning
            ? <><RefreshCw size={14} className="animate-spin" /> Crawling...</>
            : <><Play size={14} /> Crawl Now</>}
        </button>
      </div>

      {/* Running progress bar */}
      {isRunning && (
        <div data-testid="crawl-progress" className="mb-4 rounded-lg p-3 flex items-center gap-3" style={{ background: 'hsl(var(--muted))', border: '1px solid hsl(var(--border))' }}>
          <RefreshCw size={13} className="animate-spin flex-shrink-0" style={{ color: '#0891b2' }} />
          <span className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>{crawlJob?.progress || 'Crawling...'}</span>
        </div>
      )}

      {/* Domain status bar */}
      {domainStatuses.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          {domainStatuses.map(ds => (
            <div
              key={ds.domain}
              data-testid={`domain-status-${ds.domain}`}
              className="rounded-xl px-4 py-3 flex items-center justify-between"
              style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
            >
              <div className="flex items-center gap-2">
                <Globe size={13} style={{ color: '#0891b2' }} />
                <span className="text-sm font-medium" style={{ color: 'hsl(var(--foreground))' }}>{ds.domain}</span>
              </div>
              <div className="text-right">
                <div className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>
                  {ds.lastCrawled ? <>Last crawled: <strong>{fmtDate(ds.lastCrawled)}</strong></> : 'Not yet crawled'}
                </div>
                {ds.automated && ds.nextScheduled && (
                  <div className="text-xs mt-0.5" style={{ color: 'hsl(var(--muted-foreground))' }}>
                    Next: {fmtDate(ds.nextScheduled)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-0 mb-5" style={{ borderBottom: '2px solid hsl(var(--border))' }}>
        {[{ id: 'overview', label: 'Overview', icon: FileText }, { id: 'settings', label: 'Settings', icon: Settings }].map(tab => (
          <button
            key={tab.id}
            data-testid={`tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors"
            style={{
              color: activeTab === tab.id ? '#0891b2' : 'hsl(var(--muted-foreground))',
              borderBottom: activeTab === tab.id ? '2px solid #0891b2' : '2px solid transparent',
              marginBottom: '-2px',
            }}
          >
            <tab.icon size={13} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Overview Tab ────────────────────────────────────────────────────── */}
      {activeTab === 'overview' && (
        <div>
          {runsLoading ? (
            <div className="text-center py-12 text-sm" style={{ color: 'hsl(var(--muted-foreground))' }}>Loading runs...</div>
          ) : runs.length === 0 ? (
            <div className="text-center py-16 rounded-xl" style={{ background: 'hsl(var(--muted))', border: '1px solid hsl(var(--border))' }}>
              <Globe size={32} className="mx-auto mb-3 opacity-30" style={{ color: 'hsl(var(--foreground))' }} />
              <p className="text-sm font-medium" style={{ color: 'hsl(var(--foreground))' }}>No crawl runs yet</p>
              <p className="text-xs mt-1" style={{ color: 'hsl(var(--muted-foreground))' }}>Click "Crawl Now" to run your first website sync.</p>
            </div>
          ) : (
            <div className="rounded-xl overflow-hidden" style={{ border: '1px solid hsl(var(--border))' }}>
              <table className="w-full">
                <thead>
                  <tr style={{ background: 'hsl(var(--muted))' }}>
                    {['Date', 'Sites', 'Pages', 'Changes', 'Status', 'Action'].map(h => (
                      <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold" style={{ color: 'hsl(var(--muted-foreground))' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {runs.map((run, i) => (
                    <tr
                      key={run.id}
                      data-testid={`run-row-${run.id}`}
                      style={{ borderTop: i > 0 ? '1px solid hsl(var(--border))' : 'none', background: 'hsl(var(--card))' }}
                    >
                      <td className="px-4 py-3 text-xs" style={{ color: 'hsl(var(--foreground))' }}>{fmtDate(run.started_at)}</td>
                      <td className="px-4 py-3 text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>{(run.sites || []).join(', ')}</td>
                      <td className="px-4 py-3 text-xs" style={{ color: 'hsl(var(--foreground))' }}>{run.pages_crawled ?? '-'}</td>
                      <td className="px-4 py-3 text-xs" style={{ color: 'hsl(var(--foreground))' }}>
                        {run.status === 'complete'
                          ? (run.change_summary?.has_changes === false
                              ? '-'
                              : (run.pages_changed || 0) + (run.pages_new || 0) + (run.pages_removed || 0))
                          : '-'}
                      </td>
                      <td className="px-4 py-3"><RunStatusBadge run={run} /></td>
                      <td className="px-4 py-3">
                        {run.status === 'complete' && (
                          run.master_brief_updated ? (
                            <span className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>Applied</span>
                          ) : run.reviewed ? (
                            <span className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>Reviewed</span>
                          ) : (
                            <button
                              data-testid={`btn-review-run-${run.id}`}
                              onClick={() => handleOpenRun(run.id)}
                              className="px-2.5 py-1 rounded-md text-xs font-medium transition-colors"
                              style={{
                                background: run.change_summary?.has_changes ? 'rgba(217,119,6,0.1)' : 'hsl(var(--muted))',
                                color: run.change_summary?.has_changes ? '#b45309' : 'hsl(var(--muted-foreground))',
                                border: run.change_summary?.has_changes ? '1px solid rgba(217,119,6,0.3)' : '1px solid hsl(var(--border))',
                              }}
                            >
                              Review
                            </button>
                          )
                        )}
                        {run.status === 'failed' && (
                          <span className="text-xs text-red-500">Error</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Settings Tab ────────────────────────────────────────────────────── */}
      {activeTab === 'settings' && settingsForm && (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm" style={{ color: 'hsl(var(--muted-foreground))' }}>
              Configure which domains to crawl, automation schedules, and exclusion rules for each site.
            </p>
            <button
              data-testid="btn-save-settings"
              onClick={handleSaveSettings}
              disabled={!settingsDirty || savingSettings}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-50 transition-opacity"
              style={{ background: '#1e3a5f' }}
            >
              <Save size={12} />
              {savingSettings ? 'Saving...' : 'Save Settings'}
            </button>
          </div>

          {settingsForm.sites.map((site, idx) => (
            <DomainCard
              key={idx}
              site={site}
              idx={idx}
              onChange={handleSiteChange}
              onRemove={handleRemoveDomain}
              isDefault={idx < 2}
            />
          ))}

          <button
            data-testid="btn-add-domain"
            onClick={handleAddDomain}
            className="w-full py-3 rounded-xl border-2 border-dashed flex items-center justify-center gap-2 text-sm font-medium transition-colors hover:border-blue-300 hover:bg-blue-50"
            style={{ borderColor: 'hsl(var(--border))', color: 'hsl(var(--muted-foreground))' }}
          >
            <Plus size={14} />
            Add Domain
          </button>
        </div>
      )}

      {/* Run Detail Modal */}
      {selectedRunId && (
        runDetailLoading ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.4)' }}>
            <RefreshCw size={24} className="animate-spin text-white" />
          </div>
        ) : selectedRun ? (
          <RunDetailModal
            run={selectedRun}
            onClose={() => { setSelectedRun(null); setSelectedRunId(null); setApplyJobId(null); setApplyJob(null); }}
            onDismiss={handleDismiss}
            onApply={handleApply}
            onConfirmApply={handleConfirmApply}
            applyJobId={applyJobId}
            applyJob={applyJob}
          />
        ) : null
      )}
    </div>
  );
}
